import React from 'react';
import { fetcher } from '@weezevent/weezjs-core';
import { Card, ContentCard, WeezHeader, Dropdown, ToastContext, Button, Icon, ButtonDropdown } from '@weezevent/nacre';

import SalesReportHeader from './header';
import SalesTab from './salesTab';
import SearchModule from './searchModule';
import { useTranslation } from 'react-i18next';
import SalesReportFetcher from '../../../../../models/salesReport';
import { useFilters, handleSearchQuery, useSort, ManagerContext } from '../../../../manager/components/context';
import { downloadFile } from '../../../../../utils/downloadFile';
import download from '../../../../../utils/asynchronousDownload';

const spec = {
    multiple: [],
    fields: ['search', 'id_event', 'id_operator'],
    aliases: {},
    date: []
};

function fileTitle(organization, ticket_office, name, extension = 'csv') {
    const date = new Date();
    let dayOnMonth = String(date.getDate()).padStart(2, '0'); //To display 2numbers if date number is form 0 to 9
    let month = date.getMonth() + 1;
    let year = date.getFullYear();
    return `${year}${month}${dayOnMonth}-O${organization.id}-G${ticket_office.id}-${name}.${extension}`;
}

function downloadPdf(organization, ticket_office, fileName, callbackError) {
    const token = fetcher.authorizationHeader;
    const host = fetcher.endpoint;
    const myHeaders = new Headers();
    myHeaders.append('Authorization', token);
    const requestOptions = {
        method: 'GET',
        headers: myHeaders
    };
    fetch(`${host}/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/download_sales_synthesis`, requestOptions)
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            let a = document.createElement('a');
            document.body.appendChild(a);
            a.href = url;
            a.download = fileName;
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }, 0);
        })
        .catch(() => callbackError());
}

function downloadSalesReportAsyncPdf(organization, ticket_office, toast, t, callbackError) {
    const fetchUrl = fetcher.get(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/download_sales_report`).then(({ response }) => {
        toast.info(t('ticket.accounting.modal.downloading'));
        return { data_url: response };
    });
    download(() => fetchUrl, { organizationId: organization.pk }, toast, t).catch(() => callbackError());
}

const SalesReport = ({ organization, ticket_office }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [events, setEvents] = React.useState([]);
    const [filters, setFilters] = useFilters(spec);
    const [ordering, setOrdering] = useSort('event_name');
    const { setContext } = React.useContext(ManagerContext);

    const [data, loadingData] = SalesReportFetcher.useApiModel(
        {
            organizationId: organization.id,
            ticket_officeId: ticket_office.id,
            cache: false,
            allow_cache: false,
            query: {
                ...handleSearchQuery(spec, filters)
            },
            onSuccess: _data => {
                setEvents(_data.events);
            }
        },
        [ticket_office, organization, spec, filters]
    );

    const onChangeSort = ordering => {
        setOrdering(ordering);
    };

    const filteredEvents = React.useMemo(() => {
        if (!events) {
            return [];
        }

        const _filteredEvents =
            Boolean(filters?.search) && filters.search.length > 0
                ? events.filter(ev => {
                      const reg = new RegExp(filters.search, 'gi');
                      return ev.event_name.match(reg);
                  })
                : events;

        const isDescending = ordering.startsWith('-');
        const field = isDescending ? ordering.slice(1) : ordering;

        const isString = field === 'event_name';

        return _filteredEvents.sort((a, b) => {
            if (isString) {
                // Compare as strings for field 'event_name'
                if (isDescending) {
                    return b[field].localeCompare(a[field]);
                }
                return a[field].localeCompare(b[field]);
            } else {
                // Compare as numbers for fields 'count', 'count_refund' and 'sum'.
                if (isDescending) {
                    return b[field] - a[field];
                }
                return a[field] - b[field];
            }
        });
    }, [events, ordering, filters]);

    // The ManagerContext is shared across all ticket office pages and needs to be reset when the component unmounts
    React.useEffect(() => {
        return () => setContext({});
    }, []);

    return (
        <>
            <WeezHeader
                title={t('ticket.sales_report.header.name')}
                rightComponent={[
                    <ButtonDropdown
                        key="actions"
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <Dropdown.Item
                                key={1}
                                onClick={() => {
                                    const fileName = fileTitle(organization, ticket_office, 'guichet_synthesis', 'pdf');
                                    downloadPdf(organization, ticket_office, fileName, () => toast.error(t('ticket.sales_report.report.error')));
                                }}
                                item={t('ticket.sales_report.header.button_content1')}
                            />,
                            <Dropdown.Item
                                key={2}
                                onClick={() => {
                                    downloadSalesReportAsyncPdf(organization, ticket_office, toast, t, () => toast.error(t('ticket.sales_report.report.error')));
                                }}
                                item={t('ticket.sales_report.header.button_content2')}
                            />,
                            <Dropdown.Item
                                key={3}
                                onClick={() =>
                                    downloadFile(
                                        `${window.weezConfig.api.apiUrl}/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/download_sales_report_command`,
                                        fileTitle(organization, ticket_office, 'guichet_command'),
                                        'text/csv'
                                    )
                                }
                                item={t('ticket.sales_report.header.button_content3')}
                            />
                        ]}
                    />
                ]}
            >
                {t('ticket.sales_report.header.subtitle')}
            </WeezHeader>
            <SalesReportHeader currency={organization?.currency} data={data} />
            <Card>
                <ContentCard>
                    <SearchModule events={filteredEvents} ticket_office={ticket_office} filters={filters} setFilters={setFilters} loadingCount={loadingData} />
                </ContentCard>
                <SalesTab events={filteredEvents} currency={organization.currency} loadingData={loadingData} ordering={ordering} onChangeSort={onChangeSort} />
            </Card>
        </>
    );
};

export default SalesReport;
